<template>
  <div class="dashboard-page" :class="{'blurred': !geenToegang, [theme]: true}">
    <header class="site-header">
      <div class="service-bar">
        <div class="shell service-bar__inner">
          <div class="service-context" :aria-label="$t('dashboard.service.contextAria')">
            <span class="service-context__label">VLECAD&reg; Insights</span>
            <span v-if="authState.isAuthenticated" class="service-context__application">{{ application }}</span>
          </div>
          <div class="account-bar">
            <LanguageSwitcher />
            <span v-if="authState.isAuthenticated" class="account-bar__name">{{ authState.loginName }}</span>
            <span v-if="authState.isAuthenticated && !isAdmin" class="account-bar__number">{{ authState.loginNumber }}</span>
            <span v-else-if="authState.isAuthenticated" class="account-bar__number">{{ $t('dashboard.service.admin') }}</span>
            <button v-if="authState.isAuthenticated" type="button" class="service-action" @click="logoutUser">{{ $t('dashboard.service.logout') }}</button>
            <button v-if="!authState.isAuthenticated" type="button" class="service-action login-button">{{ $t('dashboard.service.login') }}</button>
          </div>
        </div>
      </div>
      <div class="main-nav">
        <div class="shell main-nav__inner">
          <button type="button" class="brand" aria-label="VLECAD Insights" @click="setVersion2">
            <img src="/logo-vlecad-horizontal.svg" width="206" height="61" alt="VLECAD" />
            <span class="brand__product">Insights</span>
          </button>
          <div class="main-nav__actions">
            <label class="notification-toggle tooltip">
              <input type="checkbox" />
              <span class="notification-toggle__visual" aria-hidden="true"></span>
              <span>{{ $t('dashboard.navigation.notifications') }}</span>
              <span class="tooltiptext">{{ $t('dashboard.navigation.notificationsHelp') }}</span>
            </label>
            <button v-if="authState.isAuthenticated" type="button" class="manage-button" @click="openModal">
              <i class="pi pi-cog" aria-hidden="true"></i>
              {{ $t('dashboard.navigation.manage') }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <main class="dashboard-main">
      <div v-if="loadingContent" class="loader-overlay" role="status" aria-live="polite">
        <div class="loader-card">
          <div class="loader-spinner" aria-hidden="true"></div>
          <div class="loader-text">{{ $t('dashboard.loading') }}</div>
        </div>
      </div>
      <div class="shell dashboard-shell">
        <section class="dashboard-intro" aria-labelledby="dashboard-title">
          <div class="dashboard-intro__copy">
            <p class="eyebrow">{{ $t('dashboard.intro.eyebrow') }}</p>
            <h1 id="dashboard-title">{{ $t('dashboard.intro.title') }}</h1>
            <p>{{ $t('dashboard.intro.description', { application: application || $t('dashboard.intro.productionFallback') }) }}</p>
          </div>
          <div class="product-switcher" :aria-label="$t('dashboard.intro.productAria')">
            <span class="product-switcher__label">{{ $t('dashboard.intro.product') }}</span>
            <div class="product-switcher__options">
              <button type="button" class="product-switcher__button" :class="{ 'product-switcher__button--active': isAutofit }" :aria-pressed="isAutofit" @click="getAutofitData">
                <img src="../assets/Autofit_logo_AnoukB-12.png" width="72" height="72" alt="" />
                <span>AutoFIT</span>
              </button>
              <button type="button" class="product-switcher__button" :class="{ 'product-switcher__button--active': !isAutofit }" :aria-pressed="!isAutofit" @click="getKorpusData">
                <img src="../assets/Logo_Korpus_Tekengebied 1 kopie.png" width="72" height="72" alt="" />
                <span>Korpus</span>
              </button>
            </div>
          </div>
        </section>

        <div v-if="showError" class="error" role="alert">
          <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
          <span>{{ message }}</span>
        </div>

        <div class="dashboard-grid">
          <section class="dashboard-card overview-card" aria-labelledby="overview-title">
            <div class="card-heading">
              <div>
                <p class="eyebrow">{{ $t('dashboard.overview.eyebrow') }}</p>
                <h2 id="overview-title">{{ $t('dashboard.overview.title') }}</h2>
              </div>
              <span class="card-heading__mark" aria-hidden="true">01</span>
            </div>
            <div class="metric-list">
              <div class="metric-item">
                <span class="metric-item__label">{{ $t('dashboard.overview.documents') }}</span>
                <strong class="metric-item__value">{{ aantalDoc }}</strong>
              </div>
              <div v-if="isAutofit" class="metric-item">
                <span class="metric-item__label">{{ $t('dashboard.overview.compositions') }}</span>
                <strong class="metric-item__value">{{ aantalComp }}</strong>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ $t('dashboard.overview.productionParts') }}</span>
                <strong class="metric-item__value">{{ aantalProdParts }}</strong>
              </div>
            </div>
          </section>

          <section class="dashboard-card analysis-card" aria-labelledby="analysis-title">
            <div class="card-heading">
              <div>
                <p class="eyebrow">{{ $t('dashboard.analysis.eyebrow') }}</p>
                <h2 id="analysis-title">{{ $t('dashboard.analysis.title') }}</h2>
              </div>
              <span class="card-heading__mark" aria-hidden="true">02</span>
            </div>
            <div class="analysis-chart">
              <ChartComponent v-if="doughnutChart && isAutofit" :type="doughnutChart.type" :data="doughnutChart.data" :options="doughnutChart.options" />
              <ChartComponent v-if="doughnutChartKorpus && !isAutofit" :type="doughnutChartKorpus.type" :data="doughnutChartKorpus.data" :options="doughnutChartKorpus.options" />
            </div>
          </section>

          <section class="dashboard-card history-card" aria-labelledby="history-title">
            <div class="card-heading card-heading--history">
              <div>
                <p class="eyebrow">{{ $t('dashboard.history.eyebrow') }}</p>
                <h2 id="history-title">{{ $t('dashboard.history.title') }}</h2>
              </div>
              <div class="segmented-control" :aria-label="$t('dashboard.history.periodAria')">
                <button type="button" class="dayweekmonth" :class="{ active: activeButton === 'day' }" @click="byDay">{{ $t('dashboard.history.byDay') }}</button>
                <button type="button" class="dayweekmonth" :class="{ active: activeButton === 'week' }" @click="byWeek">{{ $t('dashboard.history.byWeek') }}</button>
                <button type="button" class="dayweekmonth" :class="{ active: activeButton === 'month' }" @click="byMonth">{{ $t('dashboard.history.byMonth') }}</button>
              </div>
            </div>
            <div class="history-charts" :class="{ 'history-charts--split': bubbleChart && !isAutofit }">
              <div v-if="dwmChart" class="chart-panel">
                <ChartComponent :type="dwmChart.type" :data="chartData" :options="dwmChart.options" class="chart" />
              </div>
              <div v-if="bubbleChart && !isAutofit" class="chart-panel">
                <ChartComponent :type="bubbleChart.type" :data="bubbleChart.data" :options="bubbleChart.options" class="chart" />
              </div>
            </div>
            <div v-if="version2" class="date-filter">
              <span class="date-filter__label">{{ $t('dashboard.history.dateType') }}</span>
              <div class="segmented-control segmented-control--secondary">
                <button type="button" class="dayweekmonth" :class="{ active: activeDate === 'production' }" @click="byProductionStartDate">{{ $t('dashboard.history.productionStart') }}</button>
                <button type="button" class="dayweekmonth" :class="{ active: activeDate === 'assembly' }" @click="byAssemblyDate">{{ $t('dashboard.history.assembly') }}</button>
                <button type="button" class="dayweekmonth" :class="{ active: activeDate === 'delivery' }" @click="byDeliveryDate">{{ $t('dashboard.history.delivery') }}</button>
              </div>
            </div>
          </section>

          <section class="dashboard-card material-card" aria-labelledby="material-title">
            <div class="card-heading card-heading--material">
              <div>
                <p class="eyebrow">{{ $t('dashboard.material.eyebrow') }}</p>
                <h2 id="material-title">{{ $t('dashboard.material.title') }}</h2>
              </div>
              <Button icon="pi pi-external-link" :label="$t('dashboard.material.export')" class="export-button" @click="exportCSV($event)" />
            </div>
            <div class="table-scroll">
              <DataTable v-if="isAutofit" ref="dt" :value="resultArray" paginator :rows="5" :rowsPerPageOptions="[5, 10, 15]" showGridlines removableSort exportFilename="VlecadInsights" :emptyMessage="$t('dashboard.material.noData')" class="insights-table">
                <template #header>
                  <div class="table-toolbar">
                    <label for="autofit-columns">{{ $t('dashboard.material.visibleColumns') }}</label>
                    <MultiSelect id="autofit-columns" v-model="selectedColumns" :options="columns" optionLabel="header" display="chip" :placeholder="$t('dashboard.material.selectColumns')" :emptyMessage="$t('dashboard.material.noColumns')" :emptyFilterMessage="$t('dashboard.material.noResults')" />
                  </div>
                </template>
                <Column v-for="(col, index) in selectedColumns" :key="col.field + '_' + index" :field="col.field" :header="col.header" sortable style="text-align: center"></Column>
                <Column :header="$t('dashboard.material.overview')">
                  <template #body="slotProps">
                    <ChartComponent class="small-chart" :type="stackedBarChart.type" :data="getChartData(slotProps.data)" :options="stackedBarChart.options" />
                  </template>
                </Column>
              </DataTable>
              <DataTable v-else ref="dt" :value="resultArray" paginator :rows="5" :rowsPerPageOptions="[5, 10, 15]" showGridlines removableSort exportFilename="VlecadInsights" :emptyMessage="$t('dashboard.material.noData')" class="insights-table">
                <template #header>
                  <div class="table-toolbar">
                    <label for="korpus-columns">{{ $t('dashboard.material.visibleColumns') }}</label>
                    <MultiSelect id="korpus-columns" v-model="selectedColumnsKorpus" :options="columnsKorpus" optionLabel="header" display="chip" :placeholder="$t('dashboard.material.selectColumns')" :emptyMessage="$t('dashboard.material.noColumns')" :emptyFilterMessage="$t('dashboard.material.noResults')" />
                  </div>
                </template>
                <Column v-for="(col, index) in selectedColumnsKorpus" :key="col.field + '_' + index" :field="col.field" :header="col.header" sortable style="text-align: center"></Column>
              </DataTable>
            </div>
          </section>
        </div>
      </div>
    </main>

    <ManagementComponent v-model:show="isModalOpen" @close="handleModalClose" />
    <DetailComponent v-model:show="isDetailModalOpen" v-model:headerDetail="headerDetail" @close="handleDetailModalClose" />
  </div>
</template>

<script>
import ChartComponent from "@/components/ChartComponent.vue";
import { useAuth } from "@/store/auth";
import axios from "axios";
import dayjs from "dayjs";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from "primevue/button";
import ManagementComponent from '@/components/ManagementComponent.vue';
import MultiSelect from 'primevue/multiselect';
import DetailComponent from '@/components/DetailComponent.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { useRoute } from "vue-router";
import { useI18n } from 'vue-i18n';

var weekOfYear = require("dayjs/plugin/weekOfYear");
dayjs.extend(weekOfYear);

dayjs.Ls.en.weekStart = 1;

export default {
  name: 'Dashboard',
  setup() {
    const route = useRoute();
    const { locale } = useI18n({ useScope: 'global' });
    const { authState, logout } = useAuth();
    return { authState, logout, locale }
  },
  components: { ChartComponent, DataTable, Column, Button, ManagementComponent, MultiSelect, DetailComponent, LanguageSwitcher },
  data() {
    const columns = [
      { field: 'percentageNet', headerKey: 'net', header: '' },
      { field: 'percentageRest', headerKey: 'rest', header: '' },
      { field: 'percentageScrap', headerKey: 'scrap', header: '' },
      { field: 'percentageCutting', headerKey: 'cutting', header: '' },
      { field: 'materialName', headerKey: 'materialName', header: '' },
      { field: 'thickness', headerKey: 'thickness', header: '' },
      { field: 'recordCount', headerKey: 'sheets', header: '' },
      { field: 'uniqueDocumentIds', headerKey: 'orders', header: '' },
      { field: 'uniqueMachines', headerKey: 'machines', header: '' },
      { field: 'onderdelen', headerKey: 'parts', header: '' }
    ];
    const columnsKorpus = [
      { field: 'dd12', header: 'DD12' },
      { field: 'dd15', header: 'DD15' },
      { field: 'dd20', header: 'DD20' },
      { field: 'dd25', header: 'DD25' },
      { field: 'dd35', header: 'DD35' },
      { field: 'materialName', headerKey: 'materialName', header: '' },
      { field: 'thickness', headerKey: 'thickness', header: '' },
      { field: 'recordCount', headerKey: 'sheets', header: '' },
      { field: 'uniqueDocumentIds', headerKey: 'orders', header: '' },
      { field: 'productionmethod', headerKey: 'productionMethod', header: '' },
      { field: 'dd3', header: 'DD3' },
      { field: 'dd4', header: 'DD4' },
      { field: 'dd5', header: 'DD5' },
      { field: 'dd8', header: 'DD8' },
      { field: 'dd10', header: 'DD10' }
    ];

    return {
      doughnutChart: null,
      doughnutChartKorpus: null,
      bubbleChart: null,
      dwmChart: null,
      stackedBarChart: "",
      aantalDoc: 0,
      aantalComp: 0,
      aantalProdParts: 0,
      //aantalNerf: 0,
      net: 0,
      rest: 0,
      scrap: 0,
      cutting: 0,
      loginName: "",
      loginNumber: "",
      //overviewLabels: [],
      documentArray: [],
      //materialCountsArray: [],
      activeButton: 'day',
      activeDate: 'assembly',
      showError: false,
      message: "",
      lastError: null,
      chartData: null,
      labels: [],
      labelsPreviousYear: [],
      labelsNextYear: [],
      label: "",
      labelPreviousYear: "",
      labelNextYear: "",
      dayData: [],
      weekData: [],
      dayDataChart: [],
      dayDataChartPreviousYear: [],
      dayDataChartNextYear: [],
      isAutofit: true,
      resultArray: [],
      isModalOpen: false,
      isDetailModalOpen: false,
      application: "",
      columns,
      selectedColumns: columns.filter(column => ['materialName', 'thickness', 'recordCount', 'uniqueDocumentIds', 'uniqueMachines', 'onderdelen'].includes(column.field)),
      selectedColumnsMem: [],
      columnsKorpus,
      selectedColumnsKorpus: [],
      selectedColumnsKorpusMem: columnsKorpus.filter(column => ['materialName', 'thickness', 'recordCount', 'uniqueDocumentIds', 'productionmethod', 'dd3', 'dd4', 'dd5', 'dd8', 'dd10'].includes(column.field)),
      labelsDoughnutKorpus: [],
      dataDoughnutKorpus: [],
      dataBubble: [],
      bubbleColors: [],
      dateFieldMapping: { assembly: "assemblydate", delivery: "deliverydate", production: "productionstartdate" },
      headerDetail: "",
      detailTitleContext: null,
      version2: false,
      geenToegang: true,
      theme: "light",
      isAdmin: false,
      loadingContent: false
    };
  },
  watch: {
    locale() {
      this.refreshLocalizedPresentation();
    },
    async isModalOpen(newValue) {
      console.log("isModalOpen: " + newValue);
      if(!newValue) {
        const { authState } = useAuth();
        console.log("de modal is gesloten, terug data laden." + authState.customerId);

        await this.getContentData(authState.customerId, this.application);
        this.loadCharts();
        console.log("button: " + this.activeButton);
        if(this.activeButton === "day") this.byDay();
        if(this.activeButton === "week") this.byWeek();
        if(this.activeButton === "month") this.byMonth();
      }
    },
    async isDetailModalOpen(newValue) {
      console.log("isDetailModalOpen: " + newValue);
      if(!newValue) {
        const { authState } = useAuth();
        console.log("de detail modal is gesloten, terug data laden." + authState.customerId);

        //await this.getContentData(authState.customerId, this.application);
        //this.loadCharts();
      }
    },
    headerDetail(newValue) {
      console.log("headerDetail: " + newValue);
    }
  },
  created() {
    this.refreshColumnHeaders();
  },
  methods: {
    refreshColumnHeaders() {
      [...this.columns, ...this.columnsKorpus].forEach(column => {
        if(column.headerKey) {
          column.header = this.$t(`dashboard.columns.${column.headerKey}`);
        }
      });
    },
    refreshLocalizedPresentation() {
      this.refreshColumnHeaders();

      if(this.lastError) {
        this.message = this.$t(this.lastError.key, { error: this.lastError.details });
      }
      if(this.detailTitleContext) {
        this.headerDetail = this.$t('dashboard.detailsTitle', {
          year: this.detailTitleContext.year,
          period: this.getLocalizedPeriodLabel(this.detailTitleContext.period, this.detailTitleContext.granularity)
        });
      }

      if(this.doughnutChart || this.doughnutChartKorpus || this.dwmChart || this.bubbleChart) {
        this.loadCharts();
      }
      if(this.chartData) {
        this.updateChart();
      }
    },
    setDashboardError(key, error) {
      const details = JSON.stringify(error);
      this.lastError = { key, details };
      this.showError = true;
      this.message = this.$t(key, { error: details });
    },
    getLocaleCode() {
      return this.locale || 'nl';
    },
    getLocalizedHistoryLabels() {
      return this.labels.map(label => this.getLocalizedPeriodLabel(label, this.activeButton));
    },
    getLocalizedPeriodLabel(period, granularity) {
      if(granularity === 'month') {
        return this.$t(`dashboard.chart.months.${period}`);
      }
      if(granularity === 'week') {
        return this.$t('dashboard.chart.weekNumber', { number: String(period).replace(/\D/g, '') });
      }

      const localeTags = { nl: 'nl-BE', en: 'en-GB', fr: 'fr-BE', de: 'de-DE' };
      const formatter = new Intl.DateTimeFormat(localeTags[this.getLocaleCode()] || 'nl-BE', {
        day: '2-digit',
        month: 'short'
      });
      return formatter.format(new Date(`${period}T12:00:00`));
    },
    setTheme() {
      if(window.matchMedia("(prefers-color-scheme: dark)").matches) {
        this.theme = "dark";
      }
      else {
        this.theme = "light";
      }
    },
    setVersion2() {
      if(this.version2) {
        this.version2 = false;
      }
      else {
        this.version2 = true
      }
    },
    async testing(year, month) {
      console.log("testing");
      //var date = new Date("01" + month + year);
      //const res = await axios.get(this.baseUrl+"api/getAllDocumentsBySelection/"+this.authState.customerId+"/"+year+"/"+this.application+"/"+this.activeDate+"/"+(date.getMonth()+1));
      //console.log("getAllDocumentsBySelection res: " + JSON.stringify(res.data));
      //const result = res.data.filter(item => item.total_qty !== null);
      //console.log("filter res: " + JSON.stringify(result));
      localStorage.setItem("year", year);
      localStorage.setItem("month", month);
      localStorage.setItem("activeButton", this.activeButton);
      localStorage.setItem("activeDate", this.activeDate);
      this.isDetailModalOpen = true;
      this.detailTitleContext = { year, period: month, granularity: this.activeButton };
      this.headerDetail = this.$t('dashboard.detailsTitle', {
        year,
        period: this.getLocalizedPeriodLabel(month, this.activeButton)
      });
    },
    getRandomColor() {
      const palette = [
        [13, 116, 172],
        [191, 156, 89],
        [18, 54, 74],
        [95, 160, 190],
        [105, 111, 120]
      ];
      const [r, g, b] = palette[Math.floor(Math.random() * palette.length)];
      return `rgba(${r}, ${g}, ${b}, 0.6)`;
    },
    onToggle(val) {
      this.selectedColumns = this.columns.filter(col => val.includes(col));
    },
    onToggleKorpus(val) {
      this.selectedColumnsKorpus = this.columnsKorpus.filter(col => val.includes(col));
    },
    handleModalClose() {
      console.log("handleModalClose");
      this.isModalOpen = false;
    },
    handleDetailModalClose() {
      console.log("handleDetailModalClose");
      this.isDetailModalOpen = false;
    },
    openModal() {
      console.log("openModal");
      this.isModalOpen = true;
    },
    openDetailModal() {
      console.log("openDetailModal");
      this.isDetailModalOpen = true;
    },
    calculate(bedrag, aantal) {
      return Math.round(bedrag/aantal *100) / 100;
    },
    exportCSV() {
      console.log("exportCSV");
      const now = new Date();
      const offset = now.getTimezoneOffset(); // Offset in minutes
      const localTime = new Date(now.getTime() - offset * 60000); // Convert UTC to local
      const formattedDate = localTime.toISOString().slice(0, 19).replace("T", "_").replace(/:/g, "-");
      const filename = `VlecadInsights_${formattedDate}`;
      console.log("filename: " + filename);

      //this.$refs.dt.exportCSV({ filename: filename });

      if (!this.resultArray || this.resultArray.length === 0) {
        console.warn("No data available for export.");
        return;
      }

      // Extract headers from the first row of data
      //const headers = Object.keys(this.resultArray[0]).join(",");

      // Convert each object in resultArray to a CSV row
      //const rows = this.resultArray.map(row =>
      //    Object.values(row).map(value => `"${value}"`).join(",")
      //);

      var headers = [];
      var rows = [];
      // Define custom headers
      if(this.isAutofit) {
        headers = [
          'materialName', 'thickness', 'sheets', 'orders', 'machines', 'parts', 'net', 'rest', 'scrap', 'cutting'
        ].map(key => this.$t(`dashboard.columns.${key}`)).join(",");

        // Map data to match the headers
        rows = this.resultArray.map(row =>
            [
                `"${row.materialName}"`,
                `"${row.thickness}"`,
                `"${row.recordCount}"`,
                `"${row.uniqueDocumentIds}"`,
                `"${row.uniqueMachines}"`,
                `"${row.onderdelen}"`,
                `"${row.percentageNet}"`,
                `"${row.percentageRest}"`,
                `"${row.percentageScrap}"`,
                `"${row.percentageCutting}"`
            ].join(",")
        );
      }
      else {
        headers = [
          'materialName', 'thickness', 'sheets', 'orders', 'length', 'width', 'productionMethod'
        ].map(key => this.$t(`dashboard.columns.${key}`)).join(",");

        // Map data to match the headers
        rows = this.resultArray.map(row =>
            [
                `"${row.materialName}"`,
                `"${row.thickness}"`,
                `"${row.recordCount}"`,
                `"${row.uniqueDocumentIds}"`,
                `"${row.sizex}"`,
                `"${row.sizey}"`,
                `"${row.productionmethod}"`
            ].join(",")
        );
      }

      // Join headers and rows
      const csvContent = [headers, ...rows].join("\n");

      // Create a Blob and trigger the download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    updateChart() {
      console.log("updateChart");
      this.chartData = {
        labels: this.getLocalizedHistoryLabels(),
        datasets: [
          {
            label: this.labelPreviousYear,
            data: this.dayDataChartPreviousYear,
            backgroundColor: "#0D74AC",
            hoverBackgroundColor: "#095F8D"
          },
          {
            label: this.label,
            data: this.dayDataChart,
            backgroundColor: "#8FB7C9",
            hoverBackgroundColor: "#6E9EB5"
          },
          {
            label: this.labelNextYear,
            data: this.dayDataChartNextYear,
            backgroundColor: "#12364A",
            hoverBackgroundColor: "#243F4E"
          },
        ],
      };
    },
    async initData() {
      this.aantalDoc = 0;
      this.aantalComp = 0;
      this.aantalProdParts = 0;
      //this.aantalNerf = 0;
      this.documentArray = [];
      //this.materialCountsArray = [];
      this.doughnutChart = null;
      this.doughnutChartKorpus = null;
      this.bubbleChart = null;
      this.stackedBarChart = "";
      this.dwmChart = null;
      this.net = 0;
      this.rest = 0;
      this.scrap = 0;
      this.cutting = 0;
      this.dayDataChart = [];
      this.dayDataChartPreviousYear = [];
      this.dayDataChartNextYear = [];
    },
    async getAutofitData() {
      console.log("getAutofitData");
      this.isAutofit = true;
      this.selectedColumns = this.selectedColumnsMem;
      this.selectedColumnsKorpusMem = this.selectedColumnsKorpus;
      this.selectedColumnsKorpus = [];
      await this.initData();
      await this.getContentData(this.authState.customerId, "AutoFIT");
      this.loadCharts();
      this.activeButton = "day";
      this.application = "AutoFIT";
      this.activeDate = "assembly";
      localStorage.setItem("application", this.application);
      await this.byWeek();
    },
    async getKorpusData() {
      console.log("getKorpusData");
      this.isAutofit = false;
      this.selectedColumnsMem = this.selectedColumns;
      this.selectedColumns = [];
      this.selectedColumnsKorpus = this.selectedColumnsKorpusMem;
      await this.initData();
      await this.getContentData(this.authState.customerId, "korpus");
      this.loadCharts();
      this.activeButton = "day";
      this.application = "korpus";
      this.activeDate = "assembly";
      localStorage.setItem("application", this.application);
      await this.byWeek();
    },
    setActive(button) {
      this.activeButton = button;
    },
    setActiveDate(button) {
      this.activeDate = button;
    },
    generateYearAdjustedLabels(labels, yearAdjustment) {
      const lbls = [];
      labels.map(label => {
        const date = new Date(label);
        date.setFullYear(date.getFullYear() + yearAdjustment);
        lbls.push(date.toISOString().split('T')[0]);
      })

      return lbls;
    },
    generateDayLabels() {
      const labels = [];
      const today = new Date();
      this.label = today.getFullYear();
      this.labelPreviousYear = today.getFullYear() - 1;
      this.labelNextYear = today.getFullYear() + 1;

      for (let i = -3; i <= 3; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        labels.push(date.toISOString().split('T')[0]); // Format as "YYYY-MM-DD"
      }

      return labels;
    },
    generateWeekLabels() {
      const today = new Date();
      this.label = today.getFullYear();
      this.labelPreviousYear = today.getFullYear() - 1;
      this.labelNextYear = today.getFullYear() + 1;
      var weekOfYear = require("dayjs/plugin/weekOfYear");
      //dayjs.extend(weekOfYear);
      //console.log("week: " + dayjs(today).week());
      const labels = [];
      const week = dayjs(today).week();

      for (let i = -3; i <= 3; i++) {
        var weekVar = week + i;
        if(weekVar === 0) weekVar = 52;
        if(weekVar === -1) weekVar = 51;
        if(weekVar === -2) weekVar = 50;
        if(weekVar === 53) weekVar = 1;
        if(weekVar === 54) weekVar = 2;
        if(weekVar === 55) weekVar = 3;
        //labels.push("Week"+weekVar);
        labels.push(`week${String(weekVar).padStart(2, "0")}`);
      }

      return labels;
    },
    generateMonthLabels() {
      const labels = [];
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const today = new Date();
      this.label = today.getFullYear();
      this.labelPreviousYear = today.getFullYear() - 1;
      this.labelNextYear = today.getFullYear() + 1;

      for (let i = -3; i <= 3; i++) {
        const date = new Date(today.getFullYear(), today.getMonth() + i);
        const monthName = monthNames[date.getMonth()];
        labels.push(monthName);
      }

      return labels;
    },
    async byProductionStartDate() {
      this.setActiveDate('production');
      if(this.activeButton === "day") this.byDay();
      if(this.activeButton === "week") this.byWeek();
      if(this.activeButton === "month") this.byMonth();
    },
    async byAssemblyDate() {
      this.setActiveDate('assembly');
      if(this.activeButton === "day") this.byDay();
      if(this.activeButton === "week") this.byWeek();
      if(this.activeButton === "month") this.byMonth();
    },
    async byDeliveryDate() {
      this.setActiveDate('delivery');
      if(this.activeButton === "day") this.byDay();
      if(this.activeButton === "week") this.byWeek();
      if(this.activeButton === "month") this.byMonth();
    },
    async byDay() {
      console.log("byDay");
      this.labels = this.generateDayLabels();
      this.labelsPreviousYear = Array.from(this.generateYearAdjustedLabels(this.labels, -1));
      this.labelsNextYear = Array.from(this.generateYearAdjustedLabels(this.labels, 1));
      this.setActive('day');

      var dayData = [];
      var chartData = "";
      dayData = await this.getChartDataByDay(this.label);
      console.log("dayData: " + JSON.stringify(dayData));
      const selectedDateField = this.dateFieldMapping[this.activeDate] || "assemblydate";
      //console.log("selectedDateField: " + selectedDateField);
      //console.log("labels: " + this.labels);
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labels.map(label => {
          // Find the corresponding data for this label
          //const data = dayData.find(item => item.assemblydate === label);
          const data = dayData.find(item => item[selectedDateField] === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChart = Array.from(chartData);
      }
      console.log("dayDataChart: " + this.dayDataChart);
      dayData = await this.getChartDataByDay(this.labelPreviousYear);
      console.log("dayData py: " + JSON.stringify(dayData));
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labelsPreviousYear.map(label => {
          // Find the corresponding data for this label
          //const data = dayData.find(item => item.assemblydate === label);
          const data = dayData.find(item => item[selectedDateField] === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChartPreviousYear = Array.from(chartData);
      }
      console.log("dayDataChartPreviousYear: " + this.dayDataChartPreviousYear);
      dayData = await this.getChartDataByDay(this.labelNextYear);
      console.log("dayData ny: " + JSON.stringify(dayData));
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labelsNextYear.map(label => {
          // Find the corresponding data for this label
          //const data = dayData.find(item => item.assemblydate === label);
          const data = dayData.find(item => item[selectedDateField] === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChartNextYear = Array.from(chartData);
      }
      console.log("dayDataChartNextYear: " + this.dayDataChartNextYear);

      this.updateChart();
    },
    async byWeek() {
      console.log("byWeek");
      this.labels = this.generateWeekLabels();
      this.setActive('week');

      var dayData = [];
      var chartData = "";
      dayData = await this.getChartDataByWeek(this.label);
      console.log("dayData: " + JSON.stringify(dayData));
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labels.map(label => {
          // Find the corresponding data for this label
          const data = dayData.find(item => item.assemblyweek === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChart = Array.from(chartData);
      }
      console.log("dayDataChart: " + this.dayDataChart);
      dayData = await this.getChartDataByWeek(this.labelPreviousYear);
      console.log("dayData py: " + JSON.stringify(dayData));
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labels.map(label => {
          // Find the corresponding data for this label
          const data = dayData.find(item => item.assemblyweek === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChartPreviousYear = Array.from(chartData);
      }
      console.log("dayDataChartPreviousYear: " + this.dayDataChartPreviousYear);
      dayData = await this.getChartDataByWeek(this.labelNextYear);
      console.log("dayData ny: " + JSON.stringify(dayData));
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labels.map(label => {
          // Find the corresponding data for this label
          const data = dayData.find(item => item.assemblyweek === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChartNextYear = Array.from(chartData);
      }
      console.log("dayDataChartNextYear: " + this.dayDataChartNextYear);

      this.updateChart();
    },
    async byMonth() {
      console.log("byMonth");
      this.labels = this.generateMonthLabels();
      this.setActive('month');
      
      var dayData = [];
      var chartData = "";
      dayData = await this.getChartDataByMonth(this.label);
      console.log("dayData: " + JSON.stringify(dayData));
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labels.map(label => {
          // Find the corresponding data for this label
          const data = dayData.find(item => item.month === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChart = Array.from(chartData);
      }
      console.log("dayDataChart: " + this.dayDataChart);
      dayData = await this.getChartDataByMonth(this.labelPreviousYear);
      console.log("dayData py: " + JSON.stringify(dayData));
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labels.map(label => {
          // Find the corresponding data for this label
          const data = dayData.find(item => item.month === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChartPreviousYear = Array.from(chartData);
      }
      console.log("dayDataChartPreviousYear: " + this.dayDataChartPreviousYear);
      dayData = await this.getChartDataByMonth(this.labelNextYear);
      console.log("dayData ny: " + JSON.stringify(dayData));
      if(dayData !== null) {
        // Prepare the data for the chart
        chartData = this.labels.map(label => {
          // Find the corresponding data for this label
          const data = dayData.find(item => item.month === label);
          // If data exists, use its total_qty; otherwise, default to 0
          return data ? data.total_qty : 0;
        });
        this.dayDataChartNextYear = Array.from(chartData);
      }
      console.log("dayDataChartNextYear: " + this.dayDataChartNextYear);

      this.updateChart();
    },
    groupByWeek(data) {
      const weekData = {};
      const weekObj = {};
      console.log("------------------------");
      console.log("data: " + JSON.stringify(data));
      console.log("------------------------");
      const selectedDateField = this.dateFieldMapping[this.activeDate] || "assemblydate";

      //data.forEach(({ assemblydate, total_qty }) => {
      //  const today = new Date(assemblydate);
      data.forEach((item) => {
        const today = new Date(item[selectedDateField]);
        const weekNumber = dayjs(today).week();
        const weekKey = `week${String(weekNumber).padStart(2, "0")}`; // Format week number as weekXX
        weekObj.assemblyweek = weekKey;
        weekObj.total_qty = item.total_qty;
        //console.log("weekObj: " + JSON.stringify(weekObj));
        if (!weekData[weekKey]) {
          weekData[weekKey] = 0;
        }
        weekData[weekKey] += item.total_qty;
      });

      return weekData;
    },
    async getChartDataByDay(label) {
      console.log("getChartDataByDay");
      try {
        console.log("*** baseUrl: " + this.baseUrl + " ***");
        const { authState } = useAuth();
        if(this.isAdmin) {
          const res = await axios.get(this.baseUrl+"api/getAllDocumentsByDateAdmin/"+label+"/"+this.application+"/"+this.activeDate);
          return res.data;
        }
        else {
          const res = await axios.get(this.baseUrl+"api/getAllDocumentsByDate/"+authState.customerId+"/"+label+"/"+this.application+"/"+this.activeDate);
          return res.data;
        }
      } 
      catch (error) {
        console.log("getChartDataByDay error: " + JSON.stringify(error));
        return null;
      }
    },
    async getChartDataByWeek(label) {
      console.log("getChartDataByWeek");
      try {
        const { authState } = useAuth();
        let res = null;
        if(this.isAdmin) {
          res = await axios.get(this.baseUrl+"api/getAllDocumentsByDateAdmin/"+label+"/"+this.application+"/"+this.activeDate);
        }
        else {
          res = await axios.get(this.baseUrl+"api/getAllDocumentsByDate/"+authState.customerId+"/"+label+"/"+this.application+"/"+this.activeDate);
        }
        const result = this.groupByWeek(res.data);
        const resultArray = this.generateWeekLabels().map(week => ({
          assemblyweek: week,
          total_qty: result[week] || 0,
        }));

        return resultArray;
      } 
      catch (error) {
        console.log("getChartDataByWeek error: " + JSON.stringify(error));
        return null;
      }
    },
    async getChartDataByMonth(label) {
      console.log("getChartDataByMonth");
      const selectedDateField = this.dateFieldMapping[this.activeDate] || "assemblydate";
      try {
        const { authState } = useAuth();
        let res = null;
        if(this.isAdmin) {
          res = await axios.get(this.baseUrl+"api/getAllDocumentsByDateAdmin/"+label+"/"+this.application+"/"+this.activeDate);
        }
        else {
          res = await axios.get(this.baseUrl+"api/getAllDocumentsByDate/"+authState.customerId+"/"+label+"/"+this.application+"/"+this.activeDate);
        }
        console.log("getAllDocumentsByDate res: " + JSON.stringify(res.data));
        const months = { Jan: 0, Feb: 0, Mar: 0, Apr: 0, May: 0, Jun: 0, Jul: 0, Aug: 0, Sep: 0, Oct: 0, Nov: 0, Dec: 0 };
        //res.data.forEach(({ assemblydate, total_qty }) => {
        //  const monthName = new Date(assemblydate).toLocaleString("en-US", { month: "short" });
        //  months[monthName] += total_qty;
        //});
        res.data.forEach((item) => {
          const monthName = new Date(item[selectedDateField]).toLocaleString("en-US", { month: "short" });
          months[monthName] += item.total_qty;
        });
        const result = Object.entries(months).map(([month, total_qty]) => ({ month, total_qty }));

        return result;
      } 
      catch (error) {
        console.log("getChartDataByMonth error: " + JSON.stringify(error));
        return null;
      }
    },
    getChartData(item) {
      //console.log("item: " + JSON.stringify(item.percentageNet) + " - " + JSON.stringify(item.percentageRest) + " - " + JSON.stringify(item.percentageScrap) + " - " + JSON.stringify(item.percentageCutting));
      return {
        labels: [""],
        datasets: [
          {
            label: this.$t('dashboard.columns.net'),
            data: [item.percentageNet],
            backgroundColor: "#0D74AC",
            hoverBackgroundColor: "#095F8D",
            barThickness: 15
          },
          {
            label: this.$t('dashboard.columns.rest'),
            data: [item.percentageRest],
            backgroundColor: "#BF9C59",
            hoverBackgroundColor: "#D5BE8D",
            barThickness: 15
          },
          {
            label: this.$t('dashboard.columns.scrap'),
            data: [item.percentageScrap],
            backgroundColor: "#8FB7C9",
            hoverBackgroundColor: "#6E9EB5",
            barThickness: 15
          },
          {
            label: this.$t('dashboard.columns.cutting'),
            data: [item.percentageCutting],
            backgroundColor: "#12364A",
            hoverBackgroundColor: "#243F4E",
            barThickness: 15
          },
        ],
      };
    },
    logoutUser() {
      console.log("logoutUser");
      localStorage.setItem("version", "null");
      const { logout } = useAuth();
      logout();
      this.$router.push("/login");
    },
    async getContentData(customerId, application) {
      console.log("getContentData: " + customerId);
      console.log("this.baseUrl: " + this.baseUrl);
      console.log("isAdmin: " + this.isAdmin);
      console.trace("getContentData called");
      if(this.loadingContent) return;
      this.loadingContent = true;
      let response = null;

      if(this.isAdmin) {
        await axios.get(this.baseUrl+"api/getAllDocumentsByApplication/"+application)
        .then(async (res) => {
          response = res;
        })
        .catch((err) => {
          console.log("getAllDocumentIdsByApplication error: " + JSON.stringify(err));
          this.setDashboardError('dashboard.errors.documents', err);
        });
      }
      else {
        await axios.get(this.baseUrl+"api/getAllDocumentsByCustomerId/"+customerId+"/"+application)
        .then(async (res) => {
          response = res;
        })
        .catch((err) => {
          console.log("getAllDocumentIdsByCustomerId error: " + JSON.stringify(err));
          this.setDashboardError('dashboard.errors.documents', err);
        });
      }
      //.then(async (res) => {
        //console.log("getAllDocumentsByCustomerId res: " + JSON.stringify(res));
        if (!response || response.status !== 200 || !Array.isArray(response.data)) {
          this.loadingContent = false;
          return;
        }

        console.log("aantal doc: " + response.data.length);
        if(response.data.length > 0) {
          this.documentArray = response.data;
          //console.log("documentArray: " + JSON.stringify(this.documentArray));
          this.aantalDoc = response.data.length;

          const docIds =  response.data.map(item => item.id);
          //console.log("docIds: " + docIds);
          var documentIds = "";
          for(let i = 0 ; i < response.data.length ; i++) {
            if(i>0) documentIds += ", "; 
            documentIds += response.data[i].id;
          }
          //console.log("documentIds: " + documentIds);
          if(application === "AutoFIT") {
            //await axios.get(this.baseUrl+"api/getAllCompositionsByDocumentIds/"+documentIds)
            await axios.post(this.baseUrl+"api/getAllCompositions", { "documentIds": documentIds })
            .then((res) => {
              //console.log("getAllCompositionsByDocumentIds res: " + JSON.stringify(res));
              console.log("aantal comp: " + res.data.length);
              this.aantalProdParts = 0;
              if(res.status === 200) {
                // Stap 1: Groeperen op materialname en thickness
                const grouped = res.data.reduce((acc, item) => {
                  const key = `${item.materialname}-${item.materialthickness}`;
                  if (!acc[key]) {
                    acc[key] = { 
                      materialname: item.materialname, 
                      thickness: item.materialthickness, 
                      aantal: 0,
                      documentIds: new Set(),
                      machinesIds: new Set(),
                      grossTotal: 0, 
                      netTotal: 0,
                      restTotal: 0,
                      scrapTotal: 0,
                      cuttingTotal: 0,
                      onderdelen: 0
                    };
                  }
                  acc[key].aantal += 1;
                  acc[key].documentIds.add(item.documentid);
                  acc[key].machinesIds.add(item.sawused);
                  acc[key].grossTotal += item.gross;
                  acc[key].netTotal += item.net;
                  acc[key].restTotal += item.rest;
                  acc[key].scrapTotal += item.waste;
                  acc[key].cuttingTotal += (item.sawlength*item.optimdistance);
                  acc[key].onderdelen += (item.partqty*item.qty);
                  return acc;
                }, {});

                // Stap 2: Omzetten naar array en perc berekenen
                const result2 = Object.values(grouped).map(item => ({
                  materialName: item.materialname,
                  thickness: item.thickness,
                  recordCount: item.aantal,
                  uniqueDocumentIds: item.documentIds.size,
                  uniqueMachines: item.machinesIds.size,
                  percentageNet: parseFloat(((item.netTotal / item.grossTotal) * 100).toFixed(2)),
                  percentageRest: parseFloat(((item.restTotal / item.grossTotal) * 100).toFixed(2)),
                  percentageScrap: parseFloat(((item.scrapTotal / item.grossTotal) * 100).toFixed(2)),
                  percentageCutting: parseFloat(((item.cuttingTotal / item.grossTotal) * 100).toFixed(2)),
                  onderdelen: item.onderdelen
                }));
                //console.log("result2: " + JSON.stringify(result2));

                this.aantalComp = res.data.length;
                //var tel = 0;
                var totNet = 0;
                var totGross = 0;
                var totRest = 0;
                var totScrap = 0;
                var totCutting = 0;
                var totParts = 0;

                //const materialCounts = new Map();
                const materialStats = {};
                res.data.forEach(item => {
                  //if(item.grain === 1) tel++;
                  totNet += (item.net/item.gross)*100;
                  totGross += item.gross;
                  totRest += (item.rest/item.gross)*100;
                  totScrap += (item.waste/item.gross)*100;
                  totCutting += ((item.sawlength*item.optimdistance)/item.gross)*100;
                  totParts += (item.partqty*item.qty);
                });
                this.resultArray = result2;
                
                //this.aantalNerf = tel;
                this.net = totNet / res.data.length;
                this.rest = totRest / res.data.length;
                this.scrap = totScrap / res.data.length;
                this.cutting = totCutting / res.data.length;
                this.aantalProdParts = totParts;
                console.log("net: " + this.net);
                console.log("rest: " + this.rest);
                console.log("scrap: " + this.scrap);
                console.log("cutting: " + this.cutting);
              }
            })
            .catch((err) => {
              console.log("getAllCompositionsByDocumentIds error: " + JSON.stringify(err));
              this.setDashboardError('dashboard.errors.compositions', err);
            });
          }
          else {  //korpus
            //await axios.get(this.baseUrl+"api/getAllProductionPartsByDocumentIds/"+documentIds)

            const chunkSize = 500;
            const allData = [];
            for (let i = 0; i < docIds.length; i += chunkSize) {
              const chunk = docIds.slice(i, i + chunkSize);
              //console.log("chunk: " + chunk);
              const result = await axios.post(this.baseUrl+"api/getAllProductionParts", { "documentIds": chunk });
              console.log("result:", i);
              const data = Array.isArray(result.data) ? result.data : [];
              for (const item of data) {
                allData.push(item);
              }
              //allData.push(...result.data);
            }
            //await axios.post(this.baseUrl+"api/getAllProductionParts", { "documentIds": documentIds })
            //.then(async (res) => {
              //console.log("getAllCompositionsByDocumentIds res: " + JSON.stringify(res));
              console.log("aantal comp: " + allData.length);
              this.aantalComp = 0;
              if(allData.length > 0) {
                // groeperen op prod.methode
                const groupedPM = allData.reduce((accPM, item) => {
                  const keyPM = `${item.productionmethod}`;
                  if (!accPM[keyPM]) {
                    accPM[keyPM] = {
                      productionmethod: item.productionmethod,
                      aantalPM: 0
                    };
                  }
                  accPM[keyPM].aantalPM += 1;
                  return accPM;
                }, {});
                console.log("groupedPM: " + JSON.stringify(groupedPM));

                // Stap 2: Omzetten naar array
                const resultPM = Object.values(groupedPM).map(item => ({
                  productionmethod: item.productionmethod,
                  recordCountPM: item.aantalPM,
                }));

                console.log("resultPM: " + JSON.stringify(resultPM));
                var totaalPM = 0;
                resultPM.forEach(item => {
                  totaalPM += item.recordCountPM
                });
                console.log("totaalPM: " + totaalPM);

                // Stap 1: Groeperen op materialname en thickness
                const grouped = allData.reduce((acc, item) => {
                  const key = `${item.materialname}-${item.materialthickness}`;
                  if (!acc[key]) {
                    acc[key] = { 
                      materialname: item.materialname, 
                      thickness: item.materialthickness, 
                      aantal: 0,
                      documentIds: new Set(),
                      sizex: 0,
                      sizey: 0,
                      productionmethod: item.productionmethod,
                      dd3: 0,
                      dd4: 0,
                      dd5: 0,
                      dd8: 0,
                      dd10: 0,
                      dd12: 0,
                      dd15: 0,
                      dd20: 0,
                      dd25: 0,
                      dd35: 0
                    };
                  }
                  acc[key].aantal += 1;
                  acc[key].documentIds.add(item.documentid);
                  acc[key].sizex += item.sizex;
                  acc[key].sizey += item.sizey;
                  acc[key].dd3 += item.dd3;
                  acc[key].dd4 += item.dd4;
                  acc[key].dd5 += item.dd5;
                  acc[key].dd8 += item.dd8;
                  acc[key].dd10 += item.dd10;
                  acc[key].dd12 += item.dd12;
                  acc[key].dd15 += item.dd15;
                  acc[key].dd20 += item.dd20;
                  acc[key].dd25 += item.dd25;
                  acc[key].dd35 += item.dd35;
                  return acc;
                }, {});
                console.log("grouped: " + JSON.stringify(grouped));

                // Stap 2: Omzetten naar array en perc berekenen
                const result2 = Object.values(grouped).map(item => ({
                  materialName: item.materialname,
                  thickness: item.thickness,
                  recordCount: item.aantal,
                  uniqueDocumentIds: item.documentIds.size,
                  sizex: (item.sizex/item.aantal),
                  sizey: (item.sizey/item.aantal),
                  productionmethod: item.productionmethod,
                  dd3: item.dd3,
                  dd4: item.dd4,
                  dd5: item.dd5,
                  dd8: item.dd8,
                  dd10: item.dd10,
                  dd12: item.dd12,
                  dd15: item.dd15,
                  dd20: item.dd20,
                  dd25: item.dd25,
                  dd35: item.dd35,
                }));
                //console.log("result2: " + JSON.stringify(result2));

                this.labelsDoughnutKorpus = resultPM.map(item => item.productionmethod);
                console.log("labelsDoughnutKorpus: " + this.labelsDoughnutKorpus);
                this.dataDoughnutKorpus = resultPM.map(item => parseFloat(((item.recordCountPM/totaalPM)*100).toFixed(2)));
                console.log("dataDoughnutKorpus: " + this.dataDoughnutKorpus);
                
                //await axios.post(this.baseUrl+"api/getHeatmap/"+documentIds)
                await axios.post(this.baseUrl+"api/getHeatmap", { "documentIds": documentIds })
                .then((res) => {
                  console.log("getHeatmap res: " + JSON.stringify(res.data));
                  this.dataBubble = res.data;
                  this.bubbleColors = this.dataBubble.map(() => this.getRandomColor());
                })
                .catch((err) => {
                  console.log("getHeatmap error: " + JSON.stringify(err));
                });
                this.aantalProdParts = allData.length;
                //var tel = 0;
                var totNet = 0;
                var totGross = 0;
                var totRest = 0;
                var totScrap = 0;
                var totCutting = 0;

                //const materialCounts = new Map();
                const materialStats = {};
                allData.forEach(item => {
                  //if(item.grain === 1) tel++;
                  totNet += (item.net/item.gross)*100;
                  totGross += item.gross;
                  totRest += (item.rest/item.gross)*100;
                  totScrap += (item.waste/item.gross)*100;
                  totCutting += ((item.sawlength*item.optimdistance)/item.gross)*100;
                });
                
                //this.aantalNerf = tel;
                this.resultArray = result2;
                this.net = totNet / allData.length;
                this.rest = totRest / allData.length;
                this.scrap = totScrap / allData.length;
                this.cutting = totCutting / allData.length;
                console.log("net: " + this.net);
                console.log("rest: " + this.rest);
                console.log("scrap: " + this.scrap);
                console.log("cutting: " + this.cutting);
              }
            //})
            //.catch((err) => {
            //  console.log("getAllCompositionsByDocumentIds error: " + JSON.stringify(err));
            //  this.showError = true;
            //  this.message = "Fout bij het ophalen vd composities: " + JSON.stringify(err);
            //});
          }
        }
      //})
      //.catch((err) => {
      //  console.log("getAllDocumentsByCustomerId error: " + JSON.stringify(err));
      //  this.showError = true;
      //  this.message = "Fout bij het ophalen vd documenten: " + JSON.stringify(err);
      //});
      this.loadingContent = false;
    },
    loadCharts() {
      console.log("loadCharts");
      this.doughnutChart = {
        type: "doughnut",
        data: {
          labels: [
            this.$t('dashboard.columns.net'),
            this.$t('dashboard.columns.rest'),
            this.$t('dashboard.columns.cutting'),
            this.$t('dashboard.columns.scrap')
          ],
          datasets: [
            {
              //label: "total compositions",
              data: [this.net,this.rest,this.cutting,this.scrap],
              backgroundColor: ["#0D74AC", "#BF9C59", "#12364A", "#8FB7C9"],
              hoverBackgroundColor: ["#095F8D", "#D5BE8D", "#243F4E", "#6E9EB5"],
            }
          ]
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
                position: "bottom",
            },
          },
        },
      };
      this.doughnutChartKorpus = {
        type: "doughnut",
        data: {
          labels: this.labelsDoughnutKorpus,
          datasets: [
            {
              data: this.dataDoughnutKorpus,
              backgroundColor: ["#0D74AC", "#BF9C59", "#12364A", "#8FB7C9"],
              hoverBackgroundColor: ["#095F8D", "#D5BE8D", "#243F4E", "#6E9EB5"],
            }
          ]
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: "bottom",
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  let data = context.raw;
                  return `${data} %`;
                }
              }
            }
          },
        },
      }
      this.stackedBarChart = {
        type: "bar",
        options: {
          animation: false,
          responsive: true,
          indexAxis: 'y',
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: true,
              display: false,
              max: 100
            },
            y: {
              stacked: true,
              display: false
            }
          },
          plugins: {
            legend: {
              display: false
            }
          }
        }
      };
      this.dwmChart = {
        type: "bar",
        options: {
          responsive: true,
          plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
              enabled: true
            }
          },
          onHover: (event, elements) => {
            event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
          },
          onClick: (event, elements) => {
            //console.log("elements.length: " + elements[0].datasetIndex);
            if(this.version2) {
              if (elements.length > 0) {
                const jaar = elements[0].datasetIndex;
                const dwm = elements[0].index;
                console.log("chartData: " + JSON.stringify(this.chartData));
                var label = this.labels[dwm];  // Gebruik de stabiele datakey, los van de vertaalde grafiektekst.
                if(this.activeButton === "week") {
                  label = label.replace(/\D/g, '');
                }
                const value = this.chartData.datasets[jaar].data[dwm];  // Haal de waarde op (bijv. 219)
                const valuelabel = this.chartData.datasets[jaar].label;

                //alert(`Geklikt op: ${value}`);  // Toon een alert met de tooltip-informatie
                console.log("label: " + label + " - " + valuelabel + " - " + value);
                this.testing(valuelabel, label);
              }
            }
          }
        },
      };
      this.bubbleChart = {
        type: "bubble",
        data: {
          datasets: [
            {
              label: "",
              data: this.dataBubble.map(item => ({
                x: item.SizeX,
                y: item.SizeY,
                r: item.freq
              })),
              backgroundColor: this.bubbleColors,
              borderColor: this.bubbleColors.map(color => color.replace("0.6", "1")),
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'top',
              display: false
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const data = context.raw;
                  return this.$t('dashboard.chart.bubbleTooltip', {
                    length: data.x,
                    width: data.y,
                    frequency: data.r
                  });
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: this.$t('dashboard.chart.length')
              }
            },
            y: {
              title: {
                display: true,
                text: this.$t('dashboard.chart.width')
              }
            }
          }
        }
      }
    }
  },
  async mounted() {
    //localStorage.clear();
    this.setTheme();
    //window.matchMedia("(prefers-color-scheme: dark)").addEventListener('change', this.setTheme);
    console.log("theme: " + this.theme);

    this.isAdmin = localStorage.getItem("isAdmin") === "true";
    console.log("isAdmin: " + this.isAdmin);
    
    const version = localStorage.getItem("version");
    console.log("version: " + version);
    if(version === null) {
      //this.version2 = false;
      //this.geenToegang = true;
    }
    else {
      if(version.includes("2")) {
        this.version2 = true;
        this.geenToegang = true;
      }
      if(version.includes("null")) {
        this.geenToegang = false;
      } 
    }
    const { authState } = useAuth();
    console.log("dashboard mounted: " + JSON.stringify(authState));
    await this.getContentData(authState.customerId, "AutoFIT");
    this.loadCharts();
    this.activeButton = "week";
    this.application = "AutoFIT";
    this.activeDate = "assembly";
    localStorage.setItem("application", this.application);
    //this.selectedColumns = [...this.columns];

    //console.log("url: " + window.location.href);
    //if(window.location.href.includes('v2')) {
    //  this.version2 = true;
    //}

    await this.byWeek();
  }
};
</script>

<style scoped>
.dashboard-page {
  --color-navy: #12364a;
  --color-heading: #3d4049;
  --color-text: #4d535c;
  --color-muted: #696f78;
  --color-blue: #0d74ac;
  --color-blue-dark: #095f8d;
  --color-blue-light: #dcecf4;
  --color-gold: #bf9c59;
  --color-gold-light: #d5be8d;
  --color-border: #d9dde0;
  --color-background: #f4f5f4;
  --color-surface: #ffffff;
  --color-error: #a72727;
  --shadow-card: 0 18px 44px rgba(30, 52, 65, 0.11);
  --radius-small: 0.3rem;
  --radius-medium: 0.55rem;
  --radius-large: 0.85rem;
  --content-width: 75rem;
  min-width: 20rem;
  min-height: 100vh;
  overflow-x: hidden;
  color: var(--color-text);
  background: var(--color-background);
  font-family: "Fredoka", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
}

.dashboard-page,
.dashboard-page *,
.dashboard-page *::before,
.dashboard-page *::after {
  box-sizing: border-box;
}

.dashboard-page button,
.dashboard-page input {
  font: inherit;
}

.dashboard-page button {
  color: inherit;
}

.dashboard-page img {
  display: block;
  max-width: 100%;
}

.dashboard-page h1,
.dashboard-page h2,
.dashboard-page p {
  margin-top: 0;
}

.dashboard-page h1,
.dashboard-page h2 {
  color: var(--color-heading);
  font-weight: 600;
  line-height: 1.12;
  letter-spacing: -0.025em;
}

.dashboard-page h1 {
  max-width: 47rem;
  margin-bottom: 1rem;
  font-size: clamp(2.25rem, 5vw, 3.5rem);
}

.dashboard-page h2 {
  margin-bottom: 0;
  font-size: clamp(1.45rem, 2.5vw, 2rem);
}

.dashboard-page button,
.dashboard-page label {
  -webkit-tap-highlight-color: transparent;
}

.dashboard-page button:focus-visible,
.dashboard-page input:focus-visible {
  outline: 3px solid rgba(13, 116, 172, 0.3);
  outline-offset: 3px;
}

.shell {
  width: min(calc(100% - 4rem), var(--content-width));
  margin-inline: auto;
}

.site-header {
  position: relative;
  z-index: 40;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface);
}

.site-header::after {
  content: "";
  position: absolute;
  right: max(2rem, calc((100vw - var(--content-width)) / 2));
  bottom: -1px;
  width: 4.5rem;
  height: 3px;
  background: linear-gradient(90deg, var(--color-blue) 0 62%, var(--color-gold) 62% 100%);
}

.service-bar {
  color: #ffffff;
  background: var(--color-blue-dark);
  font-size: 0.85rem;
}

.service-bar__inner,
.main-nav__inner,
.service-context,
.account-bar,
.main-nav__actions {
  display: flex;
  align-items: center;
}

.service-bar__inner,
.main-nav__inner {
  justify-content: space-between;
}

.service-bar__inner {
  min-height: 2.75rem;
  gap: 1.5rem;
}

.service-context,
.account-bar {
  min-width: 0;
  gap: 0.9rem;
}

.service-context__label {
  font-weight: 600;
}

.service-context__application,
.account-bar__number {
  position: relative;
  padding-left: 0.9rem;
  color: rgba(255, 255, 255, 0.78);
}

.service-context__application::before,
.account-bar__number::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 1px;
  height: 1rem;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.35);
}

.account-bar__name,
.account-bar__number {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.service-action {
  min-height: 2.75rem;
  padding: 0 0 0 0.25rem;
  border: 0;
  cursor: pointer;
  color: #ffffff;
  background: transparent;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: var(--color-gold-light);
  text-underline-offset: 0.2em;
}

.service-action:hover {
  color: var(--color-gold-light);
}

.main-nav {
  background: rgba(255, 255, 255, 0.98);
}

.main-nav__inner {
  min-height: 5rem;
  gap: 2rem;
}

.brand {
  min-height: 3.5rem;
  display: inline-flex;
  flex: 0 1 auto;
  align-items: center;
  gap: 1rem;
  padding: 0;
  border: 0;
  cursor: pointer;
  color: var(--color-navy);
  background: transparent;
}

.brand img {
  width: 12.9rem;
  height: auto;
}

.brand__product {
  padding-left: 1rem;
  border-left: 1px solid var(--color-border);
  color: var(--color-navy);
  font-size: 1.12rem;
  font-weight: 600;
  letter-spacing: 0.035em;
}

.main-nav__actions {
  justify-content: flex-end;
  gap: 1rem;
}

.notification-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  cursor: pointer;
  color: var(--color-muted);
  font-size: 0.9rem;
  font-weight: 500;
}

.notification-toggle > input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
}

.notification-toggle__visual {
  position: relative;
  width: 2.6rem;
  height: 1.45rem;
  flex: 0 0 auto;
  border: 1px solid #bcc4c9;
  border-radius: 999px;
  background: #e7eaec;
  transition: background-color 160ms ease, border-color 160ms ease;
}

.notification-toggle__visual::after {
  content: "";
  position: absolute;
  top: 0.18rem;
  left: 0.2rem;
  width: 0.95rem;
  height: 0.95rem;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(18, 54, 74, 0.24);
  transition: transform 160ms ease;
}

.notification-toggle > input:checked + .notification-toggle__visual {
  border-color: var(--color-blue);
  background: var(--color-blue);
}

.notification-toggle > input:checked + .notification-toggle__visual::after {
  transform: translateX(1.16rem);
}

.notification-toggle > input:focus-visible + .notification-toggle__visual {
  outline: 3px solid rgba(13, 116, 172, 0.3);
  outline-offset: 3px;
}

.manage-button {
  min-height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.68rem 1.1rem;
  border: 2px solid var(--color-gold);
  border-radius: var(--radius-small);
  cursor: pointer;
  color: var(--color-navy);
  background: var(--color-gold);
  font-weight: 600;
  line-height: 1.2;
  transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.manage-button:hover {
  transform: translateY(-2px);
  border-color: var(--color-gold-light);
  background: var(--color-gold-light);
}

.dashboard-main {
  position: relative;
  min-height: calc(100vh - 7.75rem);
  padding-block: clamp(2rem, 5vw, 4.25rem);
  background:
    linear-gradient(135deg, rgba(220, 236, 244, 0.52) 0, rgba(220, 236, 244, 0) 24rem),
    var(--color-background);
}

.dashboard-shell {
  position: relative;
}

.dashboard-intro {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: clamp(2rem, 5vw, 5rem);
  margin-bottom: 2rem;
}

.dashboard-intro__copy > p:last-child {
  max-width: 44rem;
  margin-bottom: 0;
  color: var(--color-muted);
  font-size: 1.08rem;
}

.eyebrow {
  margin-bottom: 0.45rem;
  color: var(--color-blue-dark);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.product-switcher {
  min-width: 18.5rem;
}

.product-switcher__label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-muted);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.product-switcher__options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.product-switcher__button {
  position: relative;
  min-width: 8.8rem;
  min-height: 5rem;
  display: grid;
  grid-template-columns: 3rem auto;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 0.75rem;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-medium);
  cursor: pointer;
  color: var(--color-navy);
  background: var(--color-surface);
  font-weight: 600;
  box-shadow: 0 8px 22px rgba(30, 52, 65, 0.06);
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.product-switcher__button::after {
  content: "";
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 3px;
  transform: scaleX(0);
  transform-origin: left;
  background: linear-gradient(90deg, var(--color-blue) 0 68%, var(--color-gold) 68% 100%);
  transition: transform 160ms ease;
}

.product-switcher__button:hover {
  transform: translateY(-2px);
  border-color: #b7c5cc;
  box-shadow: 0 12px 28px rgba(30, 52, 65, 0.1);
}

.product-switcher__button--active {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(13, 116, 172, 0.13), 0 12px 28px rgba(30, 52, 65, 0.1);
}

.product-switcher__button--active::after {
  transform: scaleX(1);
}

.product-switcher__button img {
  width: 3rem;
  height: 3rem;
  object-fit: contain;
}

.product-switcher__button:first-child img {
  padding: 0.25rem;
  border-radius: var(--radius-small);
  background: var(--color-navy);
}

.error {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  max-height: 10rem;
  margin-bottom: 1.25rem;
  padding: 0.85rem 1rem;
  overflow: auto;
  border: 1px solid rgba(167, 39, 39, 0.25);
  border-radius: var(--radius-medium);
  color: var(--color-error);
  background: #fff5f5;
  font-size: 0.9rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(18rem, 0.85fr) minmax(24rem, 1.15fr);
  gap: 1.25rem;
  align-items: stretch;
}

.dashboard-card {
  position: relative;
  min-width: 0;
  padding: clamp(1.35rem, 3vw, 2rem);
  border: 1px solid rgba(217, 221, 224, 0.9);
  border-radius: var(--radius-large);
  background: var(--color-surface);
  box-shadow: var(--shadow-card);
}

.dashboard-card::before {
  content: "";
  position: absolute;
  top: -1px;
  left: 2rem;
  display: block;
  width: 3.5rem;
  height: 3px;
  background: linear-gradient(90deg, var(--color-blue) 0 68%, var(--color-gold) 68% 100%);
}

.card-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.card-heading .eyebrow {
  margin-bottom: 0.3rem;
}

.card-heading__mark {
  width: 3rem;
  height: 2.65rem;
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  clip-path: polygon(18% 0, 82% 0, 100% 50%, 82% 100%, 18% 100%, 0 50%);
  color: #ffffff;
  background: var(--color-blue);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.metric-list {
  display: grid;
  gap: 0;
}

.metric-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding-block: 1rem;
  border-top: 1px solid var(--color-border);
}

.metric-item:first-child {
  border-top: 0;
}

.metric-item__label {
  color: var(--color-muted);
}

.metric-item__value {
  color: var(--color-navy);
  font-size: clamp(1.7rem, 3vw, 2.2rem);
  font-weight: 600;
  line-height: 1;
}

.analysis-card {
  overflow: hidden;
}

.analysis-chart {
  min-height: 15rem;
  display: grid;
  place-items: center;
}

.analysis-chart > * {
  width: 100%;
}

:deep(.analysis-chart canvas) {
  width: 100% !important;
  max-width: 30rem !important;
  height: auto !important;
  max-height: 17rem !important;
}

.history-card,
.material-card {
  grid-column: 1 / -1;
}

.card-heading--history {
  align-items: center;
}

.segmented-control {
  display: inline-flex;
  flex: 0 0 auto;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-small);
  background: var(--color-background);
}

.dayweekmonth {
  min-height: 2.65rem;
  min-width: 6.8rem;
  padding: 0.55rem 0.85rem;
  border: 0;
  border-left: 1px solid var(--color-border);
  cursor: pointer;
  color: var(--color-muted);
  background: transparent;
  font-size: 0.9rem;
  font-weight: 600;
  transition: color 160ms ease, background-color 160ms ease;
}

.dayweekmonth:first-child {
  border-left: 0;
}

.dayweekmonth:hover {
  color: var(--color-blue-dark);
  background: var(--color-blue-light);
}

.dayweekmonth.active {
  color: #ffffff;
  background: var(--color-blue-dark);
}

.history-charts {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
}

.history-charts--split {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.chart-panel {
  min-width: 0;
  min-height: 22rem;
  display: grid;
  place-items: center;
  padding: 0.75rem;
  border: 1px solid #e7e9eb;
  border-radius: var(--radius-medium);
  background: #fbfcfc;
}

.chart-panel > * {
  width: 100%;
}

:deep(.chart-panel canvas) {
  width: 100% !important;
  max-width: none !important;
  height: auto !important;
  max-height: 23rem !important;
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--color-border);
}

.date-filter__label {
  color: var(--color-muted);
  font-size: 0.85rem;
  font-weight: 600;
}

.segmented-control--secondary .dayweekmonth {
  min-width: 7.25rem;
}

.card-heading--material {
  align-items: center;
}

:deep(.export-button.p-button) {
  min-height: 2.75rem;
  border: 2px solid var(--color-blue-dark);
  border-radius: var(--radius-small);
  color: #ffffff;
  background: var(--color-blue-dark);
  font-family: "Fredoka", "Segoe UI", sans-serif;
  font-weight: 600;
}

:deep(.export-button.p-button:hover) {
  border-color: var(--color-blue);
  background: var(--color-blue);
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scrollbar-color: var(--color-blue) #e6e8e9;
}

.insights-table {
  min-width: 54rem;
}

.table-toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.table-toolbar label {
  flex: 0 0 auto;
  color: var(--color-navy);
  font-size: 0.9rem;
  font-weight: 600;
}

:deep(.p-datatable) {
  color: var(--color-text);
  font-family: "Fredoka", "Segoe UI", sans-serif;
  font-size: 0.9rem;
}

:deep(.p-datatable-header) {
  padding: 0.85rem 1rem;
  border-color: var(--color-border);
  background: var(--color-background);
}

:deep(.p-multiselect) {
  width: min(100%, 34rem);
  border-color: #c8ced2;
  border-radius: var(--radius-small);
  box-shadow: none;
}

:deep(.p-multiselect:not(.p-disabled):hover) {
  border-color: var(--color-blue);
}

:deep(.p-datatable-thead > tr > th) {
  padding: 0.85rem 0.75rem;
  border-color: #2d5264;
  color: #ffffff;
  background: var(--color-navy);
  font-weight: 600;
  text-align: center;
}

:deep(.p-datatable-tbody > tr > td) {
  padding: 0.8rem 0.75rem;
  border-color: #e0e3e5;
  color: var(--color-text);
  background: #ffffff;
}

:deep(.p-datatable-tbody > tr:nth-child(even) > td) {
  background: #f8f9f9;
}

:deep(.p-datatable-tbody > tr:hover > td) {
  background: #edf6fa;
}

:deep(.p-paginator) {
  border-color: var(--color-border);
  background: #ffffff;
}

:deep(.p-paginator-page.p-paginator-page-selected) {
  color: #ffffff;
  background: var(--color-blue-dark);
}

.small-chart {
  width: 15rem !important;
  height: 2.25rem !important;
  margin-inline: auto;
}

:deep(.small-chart canvas) {
  width: 15rem !important;
  height: 2.25rem !important;
  max-height: 2.25rem !important;
}

.loader-overlay {
  position: fixed;
  z-index: 100;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background: rgba(244, 245, 244, 0.78);
  backdrop-filter: blur(4px);
}

.loader-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  padding: 1.75rem 2rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-large);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: var(--shadow-card);
}

.loader-spinner {
  width: 2.75rem;
  height: 2.75rem;
  border: 4px solid var(--color-blue-light);
  border-top-color: var(--color-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loader-text {
  color: var(--color-navy);
  font-weight: 600;
}

.tooltip .tooltiptext {
  position: absolute;
  z-index: 60;
  right: 0;
  bottom: calc(100% + 0.8rem);
  width: 15rem;
  padding: 0.65rem 0.75rem;
  visibility: hidden;
  border-radius: var(--radius-small);
  color: #ffffff;
  background: var(--color-navy);
  font-size: 0.82rem;
  font-weight: 400;
  line-height: 1.4;
  opacity: 0;
  box-shadow: 0 10px 24px rgba(18, 54, 74, 0.2);
  transition: opacity 160ms ease;
}

.tooltip .tooltiptext::after {
  content: "";
  position: absolute;
  top: 100%;
  right: 2rem;
  border: 0.4rem solid transparent;
  border-top-color: var(--color-navy);
}

.tooltip:hover .tooltiptext,
.tooltip:focus-within .tooltiptext {
  visibility: visible;
  opacity: 1;
}

.blurred {
  filter: blur(5px);
  pointer-events: none;
}

.dashboard-page.dark,
.dashboard-page.light {
  color: var(--color-text);
  background: var(--color-background);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 71rem) {
  .shell {
    width: min(calc(100% - 3rem), var(--content-width));
  }

  .dashboard-intro {
    align-items: center;
  }

  .dashboard-grid {
    grid-template-columns: minmax(16rem, 0.8fr) minmax(20rem, 1.2fr);
  }

  .history-charts--split {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 48rem) {
  .shell {
    width: min(calc(100% - 2.5rem), var(--content-width));
  }

  .service-context__label,
  .account-bar__number {
    display: none;
  }

  .service-context__application {
    padding-left: 0;
  }

  .service-context__application::before {
    display: none;
  }

  .main-nav__inner {
    min-height: 4.5rem;
    gap: 1rem;
  }

  .brand {
    gap: 0.65rem;
  }

  .brand img {
    width: 10.5rem;
  }

  .brand__product {
    padding-left: 0.65rem;
    font-size: 1rem;
  }

  .notification-toggle {
    display: none;
  }

  .dashboard-main {
    padding-block: 2rem;
  }

  .dashboard-intro {
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
    gap: 1.5rem;
  }

  .product-switcher {
    width: 100%;
    min-width: 0;
  }

  .dashboard-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .history-card,
  .material-card {
    grid-column: auto;
  }

  .card-heading--history {
    flex-direction: column;
    align-items: flex-start;
  }

  .segmented-control {
    width: 100%;
  }

  .dayweekmonth {
    min-width: 0;
    flex: 1 1 0;
  }

  .chart-panel {
    min-height: 18rem;
  }

  .date-filter {
    flex-direction: column;
    align-items: flex-start;
  }

  .segmented-control--secondary .dayweekmonth {
    min-width: 0;
  }
}

@media (max-width: 30rem) {
  .shell {
    width: min(calc(100% - 1.5rem), var(--content-width));
  }

  .service-bar__inner {
    gap: 0.5rem;
  }

  .service-context {
    max-width: 7rem;
  }

  .account-bar {
    gap: 0.55rem;
  }

  .account-bar__name {
    max-width: 7rem;
  }

  .main-nav__inner {
    gap: 0.65rem;
  }

  .brand img {
    width: 8.3rem;
  }

  .brand__product {
    padding-left: 0.5rem;
    font-size: 0.85rem;
  }

  .manage-button {
    min-height: 2.5rem;
    padding: 0.55rem 0.7rem;
    font-size: 0.85rem;
  }

  .dashboard-page h1 {
    font-size: 1.9rem;
  }

  .dashboard-intro__copy > p:last-child {
    font-size: 1rem;
  }

  .product-switcher__button {
    min-width: 0;
    min-height: 4.4rem;
    grid-template-columns: 2.5rem auto;
    padding: 0.5rem;
    font-size: 0.88rem;
  }

  .product-switcher__button img {
    width: 2.5rem;
    height: 2.5rem;
  }

  .dashboard-card {
    padding: 1rem;
    border-radius: var(--radius-medium);
  }

  .dashboard-card::before {
    left: 1rem;
  }

  .card-heading {
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .card-heading__mark {
    width: 2.6rem;
    height: 2.3rem;
  }

  .metric-item {
    padding-block: 0.8rem;
  }

  .metric-item__label {
    font-size: 0.9rem;
  }

  .dayweekmonth {
    min-height: 2.5rem;
    padding-inline: 0.4rem;
    font-size: 0.78rem;
  }

  .chart-panel {
    min-height: 15rem;
    padding: 0.25rem;
  }

  .card-heading--material {
    align-items: flex-start;
  }

  :deep(.export-button.p-button) {
    padding-inline: 0.75rem;
    font-size: 0.85rem;
  }

  .table-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  :deep(.p-multiselect) {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-page *,
  .dashboard-page *::before,
  .dashboard-page *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
